import React, { FC, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController,
  ChartData,
  ChartOptions
} from 'chart.js';
import { format, parseISO } from 'date-fns';
import { FinancialHealthStats } from '../../types/financialStats';
import { HEALTH_COLORS, HealthStatusCode, getHealthStatusCode, HEALTH_STATUS_TEXT } from '../../utils/healthStatus';
import { HealthLevel } from '../../types/veraScoreInputs';
import { ChartOverlayWrapper } from './ChartOverlayWrapper';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController
);

// DTI thresholds (matching DebtToIncomeChart)
const DTI_THRESHOLDS = {
  GOOD: 36,
  FAIR: 43,
  POOR: 50,
} as const;

// Status colors (matching DebtToIncomeChart)
const STATUS_COLORS = {
  Good: '#10B981', // emerald-500 - good (< 36%)
  Fair: '#F59E0B', // amber-500 - moderate (36-43%)
  High: '#FB923C', // orange-400 - higher risk (44-49%)
  Poor: '#EF4444', // red-500 - high risk (50%+)
} as const;

// Helper function to determine DTI status (matching DebtToIncomeChart)
const getDTIStatus = (dti: number) => {
  if (dti < DTI_THRESHOLDS.GOOD) return { label: 'Good', color: STATUS_COLORS.Good };
  if (dti < DTI_THRESHOLDS.FAIR) return { label: 'Fair', color: STATUS_COLORS.Fair };
  if (dti < DTI_THRESHOLDS.POOR) return { label: 'High', color: STATUS_COLORS.High };
  return { label: 'Poor', color: STATUS_COLORS.Poor };
};

// Background bands plugin for DTI chart (matching DebtToIncomeChart)
const dtiBackgroundBandsPlugin = {
  id: 'dtiBackgroundBands',
  beforeDraw: (chart: any) => {
    const { ctx, chartArea, scales } = chart;
    const { bottom, left, right } = chartArea;
    const width = right - left;
    const top = chartArea.top;
    
    // Only draw if we have valid dimensions
    if (!width || !scales.y) return;
    
    // Calculate positions for threshold lines
    const goodY = scales.y.getPixelForValue(DTI_THRESHOLDS.GOOD);
    const fairY = scales.y.getPixelForValue(DTI_THRESHOLDS.FAIR);
    const poorY = scales.y.getPixelForValue(DTI_THRESHOLDS.POOR);
    
    ctx.save();
    
    // Draw threshold lines
    ctx.lineWidth = 1.5;
    const safeRight = right - 5;
    
    // Good threshold line (36%)
    ctx.strokeStyle = STATUS_COLORS.Good;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(left, goodY);
    ctx.lineTo(safeRight, goodY);
    ctx.stroke();
    
    // Fair threshold line (43%)
    ctx.strokeStyle = STATUS_COLORS.Fair;
    ctx.beginPath();
    ctx.moveTo(left, fairY);
    ctx.lineTo(safeRight, fairY);
    ctx.stroke();
    
    // Poor threshold line (50%)
    ctx.strokeStyle = STATUS_COLORS.Poor;
    ctx.beginPath();
    ctx.moveTo(left, poorY);
    ctx.lineTo(safeRight, poorY);
    ctx.stroke();
    
    // Add a subtle bottom axis line for context
    ctx.strokeStyle = '#E2E8F0';
    ctx.setLineDash([]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(left, bottom);
    ctx.lineTo(safeRight, bottom);
    ctx.stroke();
    
    // Add top axis reference line (subtle)
    ctx.beginPath();
    ctx.moveTo(left, top);
    ctx.lineTo(safeRight, top);
    ctx.stroke();
    
    // Add threshold labels
    ctx.setLineDash([]);
    ctx.font = 'bold 10px Manrope, sans-serif';
    ctx.textAlign = 'right';
    
    // Good threshold label
    ctx.fillStyle = STATUS_COLORS.Good;
    ctx.fillText('36%', safeRight - 2, goodY - 5);
    
    // Fair threshold label
    ctx.fillStyle = STATUS_COLORS.Fair;
    ctx.fillText('43%', safeRight - 2, fairY - 5);
    
    // Poor threshold label
    ctx.fillStyle = STATUS_COLORS.Poor;
    ctx.fillText('50%', safeRight - 2, poorY - 5);
    
    // Add '0%' at the bottom for context
    ctx.fillStyle = '#94A3B8';
    ctx.fillText('0%', safeRight - 2, bottom - 5);
    
    ctx.restore();
  }
};

// Helper function to convert hex color to RGB components
const hexToRgb = (hex: string) => {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse as hex integer
  const bigint = parseInt(hex, 16);
  
  // Extract r, g, b components
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  
  return { r, g, b };
};

interface ChartExpandModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: FinancialHealthStats[];
  title: string;
  dataKey: keyof FinancialHealthStats;
  color?: string;
  yAxisFormatter?: (value: number) => string;
  min?: number;
  max?: number;
  isCurrentFinances?: boolean;
  renderOverlay?: (data: FinancialHealthStats[]) => React.ReactNode;
  customMessageOverlay?: React.ReactNode;
}

// Color constants for glassmorphic style
const CHART_COLORS = {
  primary: 'rgba(63, 103, 186, 1)',       // Main blue
  primaryLight: 'rgba(63, 103, 186, 0.7)', // Lighter blue for hover
  success: 'rgba(52, 211, 153, 1)',       // Success/improvement
  danger: 'rgba(239, 68, 68, 1)',         // Danger/decline
  neutral: 'rgba(148, 163, 184, 1)',      // Neutral/stable
  glassBg: 'rgba(255, 255, 255, 0.6)',    // Background glass effect
  glassBorder: 'rgba(255, 255, 255, 0.8)', // Border for glass components
  shadow: 'rgba(0, 0, 0, 0.1)',           // Shadow color
  grid: 'rgba(203, 213, 225, 0.4)',       // Light grid lines
  text: 'rgba(71, 85, 105, 1)',           // Main text color
  textLight: 'rgba(100, 116, 139, 0.8)',   // Secondary text color
};

// Line style options for different chart visualizations
const LINE_STYLES = {
  STRAIGHT: 0,     // Direct lines between points
  CURVED: 0.4,     // Smooth curved lines (good for trends)
};

const ChartExpandModal: FC<ChartExpandModalProps> = ({
  isOpen,
  onClose,
  data,
  title,
  dataKey,
  color = '#1C4583',
  yAxisFormatter,
  min,
  max,
  isCurrentFinances = false,
  renderOverlay,
  customMessageOverlay
}) => {
  const chartRef = useRef<ChartJS<'line', number[], string>>(null);
  const [visibleData, setVisibleData] = useState<FinancialHealthStats[]>([]);
  
  // Update visibleData when data prop changes
  useEffect(() => {
    // Get the most recent 24 months of data
    setVisibleData(data.slice(-24));
  }, [data]);
  
  // Close on escape key
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // Find key data points (peaks, valleys, significant changes)
  const findKeyDataPoints = () => {
    if (visibleData.length < 3) return [];

    let keyPoints: number[] = [];
    
    // Always include first and last points
    keyPoints.push(0);
    keyPoints.push(visibleData.length - 1);
    
    // Find peaks and valleys
    for (let i = 1; i < visibleData.length - 1; i++) {
      const prev = visibleData[i-1][dataKey] as number ?? 0;
      const current = visibleData[i][dataKey] as number ?? 0;
      const next = visibleData[i+1][dataKey] as number ?? 0;
      
      // Peak
      if (current > prev && current > next) {
        keyPoints.push(i);
      }
      // Valley
      else if (current < prev && current < next) {
        keyPoints.push(i);
      }
      // Significant change (more than 10% of range)
      else if (Math.abs(current - prev) > 10 || Math.abs(current - next) > 10) {
        keyPoints.push(i);
      }
    }
    
    // Keep only unique indices
    return [...new Set(keyPoints)].sort((a, b) => a - b);
  };

  const keyPoints = findKeyDataPoints();

  // Calculate trend line
  const calculateTrendLine = (data: number[]): number[] => {
    if (data.length <= 2) return data;
    
    // Use linear regression to calculate trend line
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumXX = 0;
    const n = data.length;
    
    // Calculate sums for linear regression formula
    for (let i = 0; i < n; i++) {
      sumX += i;
      sumY += data[i];
      sumXY += i * data[i];
      sumXX += i * i;
    }
    
    // Calculate slope and y-intercept
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    // Generate trend line points
    return data.map((_, i) => intercept + slope * i);
  };

  const chartData: ChartData<'line', number[]> = {
    labels: visibleData.map(d => d.month),
    datasets: dataKey === 'dti_ratio' ? 
      // DTI Chart with a single continuous line
      [
        {
          label: 'DTI Ratio',
          data: visibleData.map(d => (d[dataKey] as number) ?? 0),
          borderColor: '#0EA5E9', // Match the exact color from DebtToIncomeChart
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: 'rgba(255, 255, 255, 0.9)',
          pointBorderColor: '#0EA5E9', // Match the exact color from DebtToIncomeChart
          pointBorderWidth: 2,
          pointHitRadius: 10,
        },
        // Add trend line for DTI
        {
          label: 'Trend',
          data: calculateTrendLine(visibleData.map(d => (d[dataKey] as number) ?? 0)),
          fill: false,
          tension: 0.2, // Slight curve for trend line
          borderWidth: 2, // Slightly thicker line
          borderDash: [6, 4], // More visible dash pattern
          pointRadius: 0,
          borderColor: 'rgba(107, 114, 128, 0.8)', // Gray color for trend line that matches main chart
          spanGaps: false,
        }
      ] : 
      // All other charts use the default dataset structure
      [
        {
          label: title,
          data: visibleData.map(d => (d[dataKey] as number) ?? 0),
          fill: true,
          tension: LINE_STYLES.CURVED,
          borderWidth: 2.5,
          pointRadius: (ctx) => {
            // Always show points for credit score data
            if (title === "Credit Score") {
              return 3;
            }
            // For other charts, show points only for key points to reduce visual clutter
            return keyPoints.includes(ctx.dataIndex) ? 3 : 0;
          },
          pointHoverRadius: 5,
          pointBackgroundColor: 'rgba(255, 255, 255, 0.9)',
          pointBorderColor: color,
          pointBorderWidth: 2,
          spanGaps: true,
          borderCapStyle: 'round',
          borderJoinStyle: 'round',
          backgroundColor: (context) => {
            const chart = context.chart;
            const {ctx, chartArea} = chart;
            if (!chartArea) return 'rgba(66, 133, 244, 0.3)';
            
            // Create improved gradient for the area
            const areaGradient = ctx.createLinearGradient(0, 0, 0, 300);
            
            // If using VeraScore blue color
            if (dataKey === 'verascore') {
              areaGradient.addColorStop(0, 'rgba(66, 133, 244, 0.3)');  // Brighter blue at top
              areaGradient.addColorStop(0.5, 'rgba(63, 103, 186, 0.15)'); // Brand blue in middle
              areaGradient.addColorStop(1, 'rgba(255, 255, 255, 0.05)');  // Almost transparent at bottom
            } 
            // For DTI purple color
            else if (dataKey === 'factor_dti') {
              areaGradient.addColorStop(0, 'rgba(168, 85, 247, 0.3)');  // Brighter purple at top (higher opacity)
              areaGradient.addColorStop(0.5, 'rgba(168, 85, 247, 0.15)'); // Purple in middle (medium opacity)
              areaGradient.addColorStop(1, 'rgba(255, 255, 255,.05)');  // Almost transparent at bottom
            }
            // Default fallback for other charts
            else {
              const colorRgb = hexToRgb(color);
              areaGradient.addColorStop(0, `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.3)`);
              areaGradient.addColorStop(0.5, `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.15)`);
              areaGradient.addColorStop(1, 'rgba(255, 255, 255, 0.05)');
            }
            
            return areaGradient;
          },
          borderColor: (context) => {
            const chart = context.chart;
            const {ctx, chartArea} = chart;
            if (!chartArea) return color;
            
            // Create improved gradient for the line
            const lineGradient = ctx.createLinearGradient(0, 0, 0, 300);
            
            // If using VeraScore blue color
            if (dataKey === 'verascore') {
              lineGradient.addColorStop(0, 'rgba(66, 133, 244, 0.95)'); // Brighter blue at top
              lineGradient.addColorStop(0.6, 'rgba(63, 103, 186, 0.9)'); // Brand blue in middle
              lineGradient.addColorStop(1, 'rgba(55, 48, 163, 0.8)');    // Deep indigo at bottom
            }
            // For DTI purple color
            else if (dataKey === 'factor_dti') {
              lineGradient.addColorStop(0, 'rgba(168, 85, 247, 0.95)'); // Brighter purple at top
              lineGradient.addColorStop(0.6, 'rgba(168, 85, 247, 0.9)'); // Purple in middle
              lineGradient.addColorStop(1, 'rgba(147, 51, 234, 0.8)');    // Deeper purple at bottom
            }
            // Default fallback for other charts
            else {
              const colorRgb = hexToRgb(color);
              lineGradient.addColorStop(0, `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.95)`);
              lineGradient.addColorStop(0.6, `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.9)`);
              lineGradient.addColorStop(1, `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.8)`);
            }
            
            return lineGradient;
          }
        },
        // Only add trend line for non-DTI charts
        {
          label: 'Trend',
          data: calculateTrendLine(visibleData.map(d => (d[dataKey] as number) ?? 0)),
          fill: false,
          tension: 0.2, // Slight curve for trend line
          borderWidth: 2, // Slightly thicker line
          borderDash: [6, 4], // More visible dash pattern
          pointRadius: 0,
          borderColor: dataKey === 'factor_dti' 
            ? 'rgba(126, 34, 206, 0.8)' // Deeper purple for DTI trend
            : CHART_COLORS.neutral,
          spanGaps: false,
        }
      ]
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 2,
        left: 0,   // Remove left padding entirely
        right: 0,  // Remove right padding entirely
        top: 2
      }
    },
    animation: {
      duration: 750, // Smoother animations
      easing: 'easeOutQuart',
    },
    elements: {
      line: {
        tension: 0.4
      },
      point: {
        radius: 0, // Hide points by default (shown selectively with the pointRadius function)
        hitRadius: 10,
        hoverRadius: 5,
        hoverBorderWidth: 2
      }
    },
    transitions: {
      active: {
        animation: {
          duration: 150 // Slightly faster transitions
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'nearest',
      axis: 'x',
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false,
          font: {
            size: 11,
            family: "'Manrope', sans-serif",
            weight: 'bold'
          },
          color: 'rgba(100, 116, 139, 0.9)',
          padding: 1,
          callback: function(_, index) {
            try {
              const month = this.getLabelForValue(index);
              if (!month) return '';
              
              const date = parseISO(month);
              // Use a more compact format that takes less space
              return format(date, "MMM''yy");
            } catch (e) {
              return '';
            }
          }
        },
        border: {
          display: false,
        },
        offset: false,
      },
      y: {
        min: dataKey === 'dti_ratio' ? 0 : min ?? 0,
        max: dataKey === 'dti_ratio' ? 100 : max ?? 100,
        grid: {
          color: 'rgba(226, 232, 240, 0.5)',
        },
        ticks: {
          // Use $1k increments for currency values, otherwise use appropriate stepSize
          stepSize: (dataKey === 'gross_income' || dataKey === 'money_in' || 
                    dataKey === 'monthly_expenses' || dataKey === 'money_leftover' || 
                    dataKey === 'net_savings' || dataKey === 'minimum_debt_payments' ||
                    dataKey === 'liquid_reserves') ? 1000 : 
                    dataKey === 'dti_ratio' ? 10 : 
                    title === 'Credit Score' ? 50 : 20,
          font: {
            size: 12,
            family: "'Manrope', sans-serif",
            weight: 'bold'
          },
          color: 'rgba(100, 116, 139, 0.9)',
          padding: 2,
          callback: function(value) {
            // For DTI values, show percentage
            if (dataKey === 'dti_ratio') {
              return `${value}%`;
            }
            // For currency values, show simplified $1k format
            if (dataKey === 'gross_income' || dataKey === 'money_in' || 
                dataKey === 'monthly_expenses' || dataKey === 'money_leftover' || 
                dataKey === 'net_savings' || dataKey === 'minimum_debt_payments' ||
                dataKey === 'liquid_reserves') {
              const numValue = Number(value);
              if (numValue === 0) return '$0';
              if (numValue % 1000 === 0) {
                return `$${(numValue/1000)}k`;
              }
            }
            return yAxisFormatter ? yAxisFormatter(Number(value)) : value;
          }
        },
        border: {
          dash: [4, 4],
          display: false,
        },
        position: 'left',
        offset: false,
      },
    },
    plugins: {
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#334155',
        bodyColor: '#1C4583',
        borderColor: 'rgba(226, 232, 240, 0.8)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        titleMarginBottom: 8,
        footerMarginTop: 8,
        titleFont: {
          weight: 600,
          size: 12,
          family: "'Manrope', sans-serif",
        },
        bodyFont: {
          weight: 500,
          size: 12,
          family: "'Manrope', sans-serif",
        },
        callbacks: {
          title: (tooltipItems) => {
            try {
              if (!tooltipItems?.[0]?.label) return '';
              const dataIndex = tooltipItems[0].dataIndex;
              const date = parseISO(visibleData[dataIndex].month);
              return format(date, 'MMMM yyyy');
            } catch (e) {
              return tooltipItems?.[0]?.label || '';
            }
          },
          label: (context) => {
            const value = context.raw as number;
            const datasetIndex = context.datasetIndex;

            // For DTI chart, override label handling
            if (dataKey === 'dti_ratio') {
              // Skip if value is null or undefined
              if (value === null || value === undefined) return [];
              
              // For DTI, show the status and value
              const status = getDTIStatus(value);
              return [
                `Month: ${value.toFixed(1)}%`,
                `Status: ${status.label}`
              ];
            }
            
            // Different labels based on whether it's the main data or trend line
            if (datasetIndex === 1 && context.dataset.label === 'Trend') {
              // Trend line
              if (typeof value === 'number') {
                // Format based on dataKey type
                if (yAxisFormatter) {
                  return [`Average: ${yAxisFormatter(value)}`, 'Trend'];
                }
                return [`Average: ${Math.round(value)}`, 'Trend'];
              }
              return [`Average: ${value}`, 'Trend'];
            }
            
            // Main data line
            const monthData = visibleData[context.dataIndex];
            
            // Get previous month data for comparison if available
            let changeText = '';
            if (context.dataIndex > 0) {
              const prevValue = visibleData[context.dataIndex - 1][dataKey] as number ?? 0;
              const change = value - prevValue;
              changeText = `Change: ${change >= 0 ? '+' : ''}${Math.round(change)}`;
            }
            
            // Format differently based on dataKey type
            let formattedValue;
            if (dataKey === 'gross_income' || dataKey === 'money_in' || 
                dataKey === 'monthly_expenses' || dataKey === 'money_leftover' || 
                dataKey === 'net_savings' || dataKey === 'minimum_debt_payments' ||
                dataKey === 'liquid_reserves') {
              formattedValue = `$${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
            } else {
              formattedValue = Math.round(value);
            }
            
            return [
              `Month: ${formattedValue}`,
              `Status: ${monthData.payment_history_health ? 
                (monthData.payment_history_health.charAt(0).toUpperCase() + monthData.payment_history_health.slice(1).toLowerCase()) : 
                (value !== null && !isNaN(value) ? 
                  (value === 0 || (monthData.number_of_late_payments === 0 && monthData.factor_payment_history === 100) ? 
                    'Excellent' : 
                    HEALTH_STATUS_TEXT[getHealthStatusCode(value)]) :
                  HEALTH_STATUS_TEXT[getHealthStatusCode(0)])}`,
              changeText
            ].filter(Boolean);
          },
        },
      },
      legend: {
        display: false,
      },
      // DTI chart needs the background bands plugin
      ...(dataKey === 'dti_ratio' ? { dtiBackgroundBands: {} } : {}),
    },
  };

  if (!isOpen) return null;

  // Calculate score metrics for the overlay
  const calculateScoreMetrics = () => {
    if (data.length < 2) return { trend: 'stable', change: 0, momentum: 'neutral', sixMonthChange: 0 };

    const currentScore = data[data.length - 1]?.[dataKey] as number ?? 0;
    const previousScore = data[data.length - 2]?.[dataKey] as number ?? 0;
    const change = currentScore - previousScore;
    
    // Get 6-month trend for more meaningful long-term analysis
    const sixMonthsAgo = data.length > 6 ? data[data.length - 7]?.[dataKey] as number ?? 0 : 0;
    const sixMonthChange = currentScore - sixMonthsAgo;

    return {
      trend: change > 0 ? 'up' : change < 0 ? 'down' : 'stable',
      change: change,
      sixMonthChange: sixMonthChange,
    };
  };

  const metrics = calculateScoreMetrics();
  const currentScore = data[data.length - 1]?.[dataKey] as number ?? 0;
  const currentHealth = data[data.length - 1]?.verascore_health ?? HealthLevel.AT_RISK;

  // Get trend icon and color
  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return '↑';
    if (trend === 'down') return '↓';
    return '→';
  };

  // Helper function to get health color
  const getHealthColor = (health: string | null | undefined) => {
    switch (health) {
      case HealthLevel.EXCELLENT:
        return 'text-emerald-500';
      case HealthLevel.HEALTHY:
        return 'text-emerald-400';
      case HealthLevel.FAIR:
        return 'text-amber-500';
      case HealthLevel.NEEDS_IMPROVEMENT:
        return 'text-yellow-600';
      case HealthLevel.AT_RISK:
        return 'text-red-600';
      default:
        return 'text-slate-600';
    }
  };

  // Create the score overlay with glassmorphic styling
  const scoreOverlay = (
    <div 
      className="absolute px-2 py-1.5 rounded-lg border backdrop-blur-md"
      style={{
        zIndex: 2,
        left: '6rem',
        top: '1rem',
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        borderColor: 'rgba(226, 232, 240, 0.8)',
        boxShadow: 'rgba(0, 0, 0, 0.03) 0px 4px 12px, rgba(0, 0, 0, 0.05) 0px 1px 3px'
      }}
    >
      <div className="grid gap-3">
        <div>
          <div className="text-xs text-slate-500 mb-1.5 font-medium">{title} Trend</div>
          <div className="flex items-center gap-3">
            <div className="text-3xl font-bold" style={{ color: dataKey === 'factor_dti' ? '#A855F7' : '#1C4583' }}>{Math.round(currentScore)}</div>
            {dataKey === 'factor_dti' && (
              <div
                className="text-xs font-medium px-2 py-1 rounded-full"
                style={{
                  backgroundColor: `${HEALTH_COLORS[getHealthStatusCode(currentScore) as HealthStatusCode]}20`,
                  color: HEALTH_COLORS[getHealthStatusCode(currentScore) as HealthStatusCode],
                  backdropFilter: 'blur(4px)'
                }}
              >
                {HEALTH_STATUS_TEXT[getHealthStatusCode(currentScore)]}
              </div>
            )}
            {dataKey === 'verascore' && currentHealth && (
              <div className={`text-xs font-medium px-2 py-1 rounded-full ${getHealthColor(currentHealth)}`} style={{ backdropFilter: 'blur(4px)' }}>
                {currentHealth}
              </div>
            )}
          </div>
        </div>

        {/* Score Change */}
        <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
          <div>
            <div className="text-xs text-slate-500 font-medium">1-Month Change</div>
            <div className="flex items-center gap-2">
              <div
                className="text-xl font-semibold"
                style={{
                  color:
                    metrics.change >= 0
                      ? dataKey === 'factor_dti' 
                        ? '#A855F7'  // Purple for positive DTI change
                        : HEALTH_COLORS[HealthStatusCode.EXCELLENT]
                      : HEALTH_COLORS[HealthStatusCode.AT_RISK],
                }}
              >
                {getTrendIcon(metrics.trend)}
              </div>
              <div className="text-sm font-medium">
                {metrics.change >= 0 ? '+' : ''}
                {Math.round(metrics.change)}
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs text-slate-500 font-medium">6-Month</div>
            <div className="flex items-center gap-2">
              <div
                className="text-xl font-semibold"
                style={{
                  color:
                    metrics.sixMonthChange >= 0
                      ? dataKey === 'factor_dti' 
                        ? '#A855F7'  // Purple for positive DTI change
                        : HEALTH_COLORS[HealthStatusCode.EXCELLENT]
                      : HEALTH_COLORS[HealthStatusCode.AT_RISK],
                }}
              >
                {metrics.sixMonthChange >= 0 ? '↑' : '↓'}
              </div>
              <div className="text-sm font-medium">
                {metrics.sixMonthChange >= 0 ? '+' : ''}
                {Math.round(metrics.sixMonthChange)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Create a data insights overlay for current finances charts
  const renderDataInsightsOverlay = () => {
    // Render the appropriate overlay
    if (isCurrentFinances && data.length > 0) {
      // Special handling for DTI Ratio
      if (dataKey === 'dti_ratio') {
        const dtiStatus = getDTIStatus(currentScore);
        const trendDirection = metrics.change < 0 ? 'positive' : metrics.change > 0 ? 'negative' : 'neutral';
        const trendLabel = metrics.change < 0 ? 'Improving' : metrics.change > 0 ? 'Increasing' : 'Stable';
        
        return (
          <div className="absolute px-4 py-3 rounded-lg border backdrop-blur-md"
            style={{
              zIndex: 2,
              right: '6rem',  // Changed: Position on the right side
              top: '1rem',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderColor: 'rgba(226, 232, 240, 0.8)',
              boxShadow: 'rgba(0, 0, 0, 0.03) 0px 4px 12px, rgba(0, 0, 0, 0.05) 0px 1px 3px'
            }}>
            <div className="grid gap-4">
              <div>
                <div className="text-xs text-slate-500 mb-1">Current DTI Ratio</div>
                <div className="flex items-center gap-2">
                  <div className="text-3xl font-bold text-brand-blue">
                    {currentScore.toFixed(1)}%
                  </div>
                  <div 
                    className="text-xs font-medium px-2 py-1 rounded-full"
                    style={{ 
                      backgroundColor: `${dtiStatus.color}15`, 
                      color: dtiStatus.color 
                    }}
                  >
                    {dtiStatus.label}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between gap-8 border-t border-slate-100 pt-3">
                <div>
                  <div className="text-xs text-slate-500">Monthly Change</div>
                  {metrics.change === 0 ? (
                    <div className="text-sm font-medium">0.0%</div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div 
                        className="text-2xl font-semibold" 
                        style={{ 
                          color: trendDirection === 'positive' ? '#10B981' : 
                                 trendDirection === 'negative' ? '#EF4444' : '#64748B'
                        }}
                      >
                        {metrics.change < 0 ? '↓' : '↑'}
                      </div>
                      <div className="text-sm font-medium">{Math.abs(metrics.change).toFixed(1)}%</div>
                    </div>
                  )}
                </div>
                
                <div>
                  <div className="text-xs text-slate-500">Trend</div>
                  <div className="text-sm font-medium">{trendLabel}</div>
                </div>
              </div>
            </div>
          </div>
        );
      }
      
      return (
        <div className="absolute px-4 py-3 rounded-lg border backdrop-blur-md"
          style={{
            zIndex: 2,
            right: '6rem', // Changed: Position on the right side
            top: '1rem',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderColor: 'rgba(226, 232, 240, 0.8)',
            boxShadow: 'rgba(0, 0, 0, 0.03) 0px 4px 12px, rgba(0, 0, 0, 0.05) 0px 1px 3px'
          }}>
          <div className="grid gap-4">
            {/* Current Value */}
            <div>
              <div className="text-xs text-slate-500 mb-1">Current {title}</div>
              <div className="text-3xl font-bold text-brand-blue">
                {dataKey === 'gross_income' || dataKey === 'money_in' || dataKey === 'monthly_expenses' || 
                  dataKey === 'money_leftover' || dataKey === 'net_savings' || dataKey === 'minimum_debt_payments' ?
                  `$${currentScore.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` :
                  `${Math.round(currentScore).toLocaleString()}`
                }
              </div>
            </div>

            {/* Monthly Change & Trend */}
            <div className="flex items-center justify-between gap-8 border-t border-slate-100 pt-3">
              <div>
                <div className="text-xs text-slate-500">Monthly Change</div>
                <div className="flex items-center gap-2">
                  {metrics.change === 0 ? (
                    <div className="text-sm font-medium">
                      {dataKey === 'gross_income' || dataKey === 'money_in' || dataKey === 'monthly_expenses' ?
                        '$0.00' : '0'
                      }
                    </div>
                  ) : (
                    <>
                      <div
                        className="text-2xl font-semibold"
                        style={{ color: metrics.change >= 0 ? '#10B981' : '#EF4444' }}
                      >
                        {metrics.change >= 0 ? '↑' : '↓'}
                      </div>
                      <div className="text-sm font-medium">
                        {dataKey === 'gross_income' || dataKey === 'money_in' || dataKey === 'monthly_expenses' ?
                          `$${Math.abs(metrics.change).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` :
                          Math.abs(Math.round(metrics.change)).toLocaleString()
                        }
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs text-slate-500">Momentum</div>
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium">
                    {metrics.trend === 'up' ? 'Growing' : metrics.trend === 'down' ? 'Declining' : 'Stable'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Call createPortal to render modal outside the component tree
  return createPortal(
    <>
      {isOpen && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center p-4 bg-slate-900/90">
          <div className="bg-white/95 backdrop-blur-lg rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-xl border border-white/40 relative">
            {/* Modal header with close button */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-semibold text-slate-800">{title} Trend</h2>
                <p className="text-sm text-slate-500 mt-1">Last 24 Months</p>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-slate-100 rounded-full transition-colors text-slate-500 hover:text-slate-700"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal content - Chart */}
            <div className="px-6 py-4 h-[70vh]">
              <div className="bg-slate-50/60 backdrop-blur-sm p-4 rounded-lg border border-slate-100 h-full">
                {/* Overlay showing appropriate data insights */}
                <ChartOverlayWrapper
                  overlay={isCurrentFinances ? 
                    (renderOverlay ? renderOverlay(visibleData) : renderDataInsightsOverlay()) : 
                    scoreOverlay
                  } 
                  showStatsButton={true}
                >
                  <div className="relative h-full">
                    <Chart
                      ref={chartRef}
                      type="line"
                      data={chartData}
                      options={options}
                      plugins={dataKey === 'dti_ratio' ? [dtiBackgroundBandsPlugin] : []}
                    />
                    {/* Show custom message overlay if provided */}
                    {customMessageOverlay && (
                      <div className="absolute inset-0 z-10 pointer-events-none">
                        {customMessageOverlay}
                      </div>
                    )}
                  </div>
                </ChartOverlayWrapper>
              </div>
            </div>
            
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default ChartExpandModal; 